import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdjustStockRequest,
  CreateInventoryPartRequest,
  CreateRestockRequest,
  PatchInventoryPartRequest,
  ReactivateInventoryPartRequest,
  StockMoveRequest,
} from '@wf/shared'
import { api } from '../api/client'

// Alle Inventar-Mutationen invalidieren die Inventar-Queries (Liste + kritische Teile); restock zusätzlich das Board.
function useInventoryMutation<V, R>(run: (vars: V) => Promise<R>, alsoProcurement = false) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory'] })
      if (alsoProcurement) void qc.invalidateQueries({ queryKey: ['procurement'] })
    },
  })
}

export function useInventory() {
  return useQuery({ queryKey: ['inventory', 'list'], queryFn: api.listInventory })
}
export function useCriticalParts() {
  return useQuery({ queryKey: ['inventory', 'critical'], queryFn: api.criticalParts })
}
export function useArchivedInventory(enabled: boolean) {
  return useQuery({ queryKey: ['inventory', 'archived'], queryFn: api.archivedInventory, enabled })
}
/** Live-Suche im Inventar (für die Teile-Sektion am Auftrag). Erst ab 2 Zeichen, matcht Name/SNR/Hersteller. */
export function useInventorySearch(query: string) {
  const q = query.trim()
  return useQuery({ queryKey: ['inventory', 'search', q], queryFn: () => api.searchInventory(q), enabled: q.length >= 2 })
}

export function useCreatePart() {
  return useInventoryMutation((body: CreateInventoryPartRequest) => api.createInventoryPart(body))
}
export function usePatchPart() {
  return useInventoryMutation((vars: { id: number; body: PatchInventoryPartRequest }) => api.patchInventoryPart(vars.id, vars.body))
}
export function useReactivatePart() {
  return useInventoryMutation((vars: { id: number; body: ReactivateInventoryPartRequest }) =>
    api.reactivateInventoryPart(vars.id, vars.body),
  )
}
export function useArchivePart() {
  return useInventoryMutation((id: number) => api.archiveInventoryPart(id))
}
export function useAdjustStock() {
  return useInventoryMutation((vars: { id: number; body: AdjustStockRequest }) => api.adjustStock(vars.id, vars.body))
}
export function useWithdrawStock() {
  return useInventoryMutation((vars: { id: number; body: StockMoveRequest }) => api.withdrawStock(vars.id, vars.body))
}
export function useAddStock() {
  return useInventoryMutation((vars: { id: number; body: StockMoveRequest }) => api.addStock(vars.id, vars.body))
}
/** „Zu bestellen" aus dem Inventar → restock-Eintrag aufs Einkauf-Board (invalidiert auch das Board). */
export function useCreateRestock() {
  return useInventoryMutation((body: CreateRestockRequest) => api.createRestock(body), true)
}
